import { useMemo } from 'react';
import WidgetHeader from '@/components/common/WidgetHeader';
import { useLocalization } from '@/hooks/useLocalization';
import { Box, Typography, useMediaQuery } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import {
  DataGridPremium,
  GridColDef,
  GridListViewColDef,
  GridRenderCellParams,
  GRID_TREE_DATA_GROUPING_FIELD,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { mobileWidth, mobileWidthValue } from '@/constants';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetPerformanceReportsQuery,
  PerformanceReportItem,
  PerformanceReportConfig,
} from '@/store/performanceReportsApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatOptionalDayjs, parseOptionalDate } from '@/utils/date';
import { useGridColumns } from '@/hooks/useGridColumns';
import { GRID_KEYS } from '@/constants/gridKeys';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { useAutoResizeColumns } from '@/hooks/useAutoResizeColumns';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import { Capacitor } from '@capacitor/core';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { ContentContainer } from '../contentContainer';
import { GridContainer } from '@/components/grids/GridContainer';

type Row = PerformanceReportItem & {
  id: number;
  treePath: string[];
};

const percentFormatter = (value?: number | null) => {
  if (value === null || value === undefined) return '';
  return `${Number(value).toFixed(2)}%`;
};

const getNotHeldDisplay = (
  symbol: PerformanceReportConfig['NotHeldSymbol'],
) => {
  switch (symbol) {
    case 'Blank':
      return '';
    case 'Dash':
      return '--';
    case 'N/A':
    default:
      return 'N/A';
  }
};

const PerformanceReport = ({
  isWidget = false,
  title,
  container = 'page',
}: StandardWidgetProps) => {
  const { t } = useLocalization();
  const widgetTitle = title ?? t('performance');
  const isMobile = useMediaQuery(mobileWidth);
  const { ref: widgetWidthRef, isBelow: isWidgetBelowXsWidth } =
    useWidgetWidthIsBelow(isWidget, mobileWidthValue);
  const shouldUseListView = isMobile || (isWidget && isWidgetBelowXsWidth);
  const apiRef = useGridApiRef();
  const authLoaded = useAuthIsLoaded();

  const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';

  const { data, isLoading, isFetching, isError } =
    useGetPerformanceReportsQuery(authLoaded ? undefined : skipToken);

  const notHeldDisplay = useMemo(
    () => getNotHeldDisplay(data?.Item2?.NotHeldSymbol || 'N/A'),
    [data],
  );

  const rows: Row[] = useMemo(() => {
    const items = data?.Item1 ?? [];
    if (!items.length) return [];
    const byId = new Map<number, PerformanceReportItem>();
    items.forEach((it) => byId.set(it.UPRAccountBenchmarkId, it));

    const memoPath = new Map<number, string[]>();
    const buildPath = (it: PerformanceReportItem): string[] => {
      const cached = memoPath.get(it.UPRAccountBenchmarkId);
      if (cached) return cached;
      const selfLabel = (it.Description || '').trim();
      const parentId = it.ParentId ?? null;
      if (!parentId) {
        const path = [selfLabel];
        memoPath.set(it.UPRAccountBenchmarkId, path);
        return path;
      }
      const parent = parentId ? byId.get(parentId) : undefined;
      const path = parent ? [...buildPath(parent), selfLabel] : [selfLabel];
      memoPath.set(it.UPRAccountBenchmarkId, path);
      return path;
    };

    return items.map((it) => ({
      ...it,
      id: it.UPRAccountBenchmarkId,
      treePath: buildPath(it),
    }));
  }, [data]);

  const config = data?.Item2;

  const { data: gridColumnsData, isLoading: gridColumnsLoading } =
    useGridColumns(GRID_KEYS.INVESTOR_PERFORMANCE);

  const normalizeFieldKey = (s: string) =>
    String(s || '')
      .trim()
      .toLowerCase();

  const labelMap = useMemo(() => {
    const map = new Map<string, string>();
    const records = gridColumnsData?.Records ?? [];
    for (const r of records) {
      const field = normalizeFieldKey(r.GridColumnDataField);
      const label = ((r.Label ?? '') as string).trim();
      if (field) map.set(field, label);
    }
    return map;
  }, [gridColumnsData]);

  const disabledSet = useMemo(() => {
    const set = new Set<string>();
    const records = gridColumnsData?.Records ?? [];
    for (const r of records) {
      if (!r.IsEnabled) set.add(normalizeFieldKey(r.GridColumnDataField));
    }
    return set;
  }, [gridColumnsData]);

  const periodCell =
    (periodKey: keyof Row, benchKey: keyof Row) =>
    (params: GridRenderCellParams<Row>) => {
      const value = params.row[periodKey] as number | null | undefined;
      const bench = params.row[benchKey] as number | null | undefined;
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant='body2'
            sx={{ fontWeight: 600 }}
          >
            {value !== null && value !== undefined
              ? percentFormatter(value)
              : notHeldDisplay}
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              fontStyle: 'italic',
              display: { xs: 'none', sm: 'none', md: 'block' },
            }}
          >
            {bench !== null && bench !== undefined
              ? percentFormatter(bench)
              : ''}
          </Typography>
        </Box>
      );
    };

  const columnsBase: GridColDef<Row>[] = [
    {
      field: 'Description',
      headerName: t('description'),
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const { Description, BenchmarkName } = params.row;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant='body2'
              sx={{ fontWeight: 700 }}
            >
              {Description}
            </Typography>
            {BenchmarkName ? (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  fontStyle: 'italic',
                  pl: 1,
                  display: { xs: 'none', sm: 'none', md: 'block' },
                }}
              >
                {BenchmarkName}
              </Typography>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'EndingMarketValue',
      headerName: t('grid.marketValue'),
      type: 'number',
      width: isMobile ? 140 : 170,
      renderCell: (params) =>
        params.value == null
          ? ''
          : formatNumberToCurrency(Number(params.value)),
    },
    {
      field: 'PerformancePeriod1',
      headerName: t('performanceReport.columns.period1'),
      width: 140,
      renderCell: periodCell('PerformancePeriod1', 'BenchmarkPeriod1'),
    },
    {
      field: 'PerformancePeriod2',
      headerName: t('performanceReport.columns.period2'),
      width: 140,
      renderCell: periodCell('PerformancePeriod2', 'BenchmarkPeriod2'),
    },
    {
      field: 'PerformancePeriod3',
      headerName: t('performanceReport.columns.period3'),
      width: 140,
      renderCell: periodCell('PerformancePeriod3', 'BenchmarkPeriod3'),
    },
    {
      field: 'PerformancePeriod4',
      headerName: t('performanceReport.columns.period4'),
      width: 140,
      renderCell: periodCell('PerformancePeriod4', 'BenchmarkPeriod4'),
    },
    {
      field: 'PerformancePeriod5',
      headerName: t('performanceReport.columns.period5'),
      width: 140,
      renderCell: periodCell('PerformancePeriod5', 'BenchmarkPeriod5'),
    },
    {
      field: 'LastUpdated',
      headerName: t('grid.lastUpdated'),
      type: 'date',
      width: isMobile ? 120 : 150,
      valueGetter: (v) => parseOptionalDate(v),
      renderCell: (params) => formatOptionalDayjs(params.value, 'M/D/YY', ''),
    },
  ];

  const columns: GridColDef<Row>[] = useMemo(() => {
    if (!config) return columnsBase;
    const enabled = (flag?: boolean) => flag === undefined || flag === true;
    const filtered = columnsBase.filter((col) => {
      const fieldKey = normalizeFieldKey(String(col.field));
      if (disabledSet.has(fieldKey)) return false;

      if (col.field === 'LastUpdated') {
        return true;
      }
      const match = /^PerformancePeriod([1-5])$/.exec(String(col.field));
      if (match) {
        const suffix = isWidget ? 'DashboardEnabled' : 'Enabled';
        const key =
          `PerformancePeriod${match[1]}${suffix}` as keyof PerformanceReportConfig;
        const flag = config
          ? ((
              config as unknown as Record<string, boolean | string | undefined>
            )[key] as boolean | undefined)
          : undefined;
        return enabled(flag);
      }
      return true;
    });
    const withLabels = filtered.map((c) => {
      const key = normalizeFieldKey(String(c.field));
      if (labelMap.has(key)) {
        return { ...c, headerName: labelMap.get(key) as string };
      }
      return c;
    });

    return withLabels;
  }, [columnsBase, config, isWidget, disabledSet, labelMap]);

  useAutoResizeColumns({
    apiRef,
    columns,
    deps: [data],
    preferredField: 'Name',
  });

  const listViewColDef: GridListViewColDef<Row> = {
    field: 'listColumn',
    renderCell: (params) => {
      const { row, api } = params;
      const visibilityModel = api?.state?.columns?.columnVisibilityModel || {};
      const showDescription = visibilityModel.Description !== false;
      const showEndingMarketValue = visibilityModel.EndingMarketValue !== false;
      return (
        <Box sx={{ width: '100%', p: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            {showDescription ? (
              <Typography
                variant='body1'
                sx={{
                  fontWeight: 600,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.2,
                }}
              >
                {row.Description}
              </Typography>
            ) : (
              <span />
            )}
            {showEndingMarketValue ? (
              <Typography
                variant='body1'
                sx={{
                  fontWeight: 600,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.2,
                }}
              >
                {row.EndingMarketValue != null
                  ? formatNumberToCurrency(Number(row.EndingMarketValue))
                  : ''}
              </Typography>
            ) : null}
          </Box>
        </Box>
      );
    },
  };

  const initialColumnVisibilityModel = useMemo(() => {
    if (isMobile) {
      const model: Record<string, boolean> = {};
      columns.forEach((c) => {
        const field = String(c.field);
        model[field] = field === 'Description' || field === 'EndingMarketValue';
      });
      // Hide the tree-data grouping column on mobile
      model[GRID_TREE_DATA_GROUPING_FIELD] = false;
      return model;
    }
    return {
      LastUpdated: true,
    } as Record<string, boolean>;
  }, [isMobile, columns]);

  const CustomGroupingCell = (params: GridRenderCellParams<Row>) => {
    const node = (
      params as unknown as {
        rowNode?: { type?: string; depth?: number; childrenExpanded?: boolean };
      }
    )?.rowNode;
    if (!node || node.type !== 'group') {
      return null;
    }

    const isExpanded = !!node.childrenExpanded;
    const toggle = () =>
      apiRef.current?.setRowChildrenExpansion(params.id, !isExpanded);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box>
          <Box
            onClick={toggle}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              p: 0.25,
            }}
          >
            {isExpanded ? (
              <ExpandMore fontSize='small' />
            ) : (
              <ChevronRight fontSize='small' />
            )}
          </Box>
        </Box>
        {node.depth === 0 ? (
          <Typography
            variant='body2'
            sx={{ fontWeight: 600 }}
          >
            {String(params.value ?? '')}
          </Typography>
        ) : null}
      </Box>
    );
  };

  if (isError) {
    return (
      <ContentContainer container={container}>
        <WidgetHeader title={widgetTitle} />
        <Box sx={{ width: '100%', py: 2 }}>
          <Typography color='error'>
            {t('errorLoadingData', 'Error loading data.')}
          </Typography>
        </Box>
      </ContentContainer>
    );
  }

  if (rows.length === 0 && authLoaded && !isLoading && !isFetching) {
    return (
      <ContentContainer container={container}>
        <WidgetHeader title={widgetTitle} />
        <Box sx={{ width: '100%', py: 2 }}>
          <Typography>
            {t('performanceReport.noReports', 'No Performance Reports Found.')}
          </Typography>
        </Box>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer
      container={container}
      sx={{
        gap: 4,
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}
    >
      <WidgetHeader title={widgetTitle} />
      <Box
        ref={widgetWidthRef}
        sx={{ flex: 1, minHeight: 0 }}
      >
        <GridContainer isWidget={isWidget}>
          <DataGridPremium
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            treeData
            getTreeDataPath={(row) => row.treePath}
            disableRowSelectionOnClick
            showToolbar
            disablePivoting
            groupingColDef={{
              renderCell: CustomGroupingCell,
              width: undefined, // Let it auto-size
              flex: 1,
              minWidth: 200,
            }}
            defaultGroupingExpansionDepth={-1}
            slotProps={{
              toolbar: {
                ...defaultToolbarExportSlotProps,
                showQuickFilter: true,
              },
            }}
            slots={{
              ...(isNative
                ? {
                    toolbar: (props: any) => (
                      <CustomToolbarForNative {...props} />
                    ),
                  }
                : shouldUseListView
                  ? { toolbar: XsPreferencesDrawerToolbar }
                  : {}),
            }}
            hideFooter
            listView={shouldUseListView}
            {...(shouldUseListView ? { listViewColumn: listViewColDef } : {})}
            loading={
              isLoading || isFetching || !authLoaded || gridColumnsLoading
            }
            initialState={{
              columns: {
                columnVisibilityModel: initialColumnVisibilityModel,
              },
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                py: 1,
                display: 'flex',
                alignItems: 'center',
              },
              // Ensure text wraps for all cell contents (custom and default)
              '& .MuiDataGrid-cell *, & .MuiDataGrid-cellContent': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.2,
              },
              // Vertically center the tree-data toggle caret
              '& .MuiDataGrid-treeDataGroupingCellToggle': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
              '& .MuiDataGrid-cellContent': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.2,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.2,
              },
            }}
          />
        </GridContainer>
      </Box>
      {config?.DisclaimerString ? (
        <Box sx={{ mt: 2, fontStyle: 'italic' }}>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {config.DisclaimerString}
          </Typography>
        </Box>
      ) : null}
    </ContentContainer>
  );
};

export default PerformanceReport;
